import React, { forwardRef, useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'
import {
  categoryGroupTagsState,
  InGameAccountModalState,
  isOpenGuestPaymentState,
  isOpenInGameAccountModalState,
  isOpenSignUpModalState,
  selectCategoryProfileState,
  selectedProductAddOnsState,
  selectedProductState,
} from '../../common/atoms/checkout'
import { useTranslation } from 'next-i18next'
import { CategoryProfileResponse } from '../../types/users/category-profile-response'
import { InGameAccountModalStatusEnum } from '../../common/enums/in-game-account-modal-status.enum'
import Image from 'next/image'
import { usePostCreateCategoryProfile } from '../../hooks/users/usePostCreateCategoryProfile'
import { CategoryResponse } from '../../types/categories/responses/category-response'
import { usePutUpdateCategoryProfile } from '../../hooks/users/usePutUpdateCategoryProfile'
import { SubmitHandler, useForm } from 'react-hook-form'
import { CategorySpecializedTag } from '../../types/users/requests/general-category-request'
import { toast } from 'react-toastify'
import groupBy from 'lodash.groupby'
import { homeStepsState } from '../../common/atoms/atoms'
import { SessionStatusEnum } from '../../common/enums/session-status.enum'
import { signIn, useSession } from 'next-auth/react'
import TrashIcon from '../../public/static/trash_gray3.svg'
import { useDeleteCategoryById } from '../../hooks/users/useDeleteCategoryById'
import { updateStoredControl } from '../../hooks/cache/user-control-storage'
import { UserLocalstorage } from '../../types/users/user-local-storage'
import { GameCategories } from '../../common/enums/game-categories'
import PreferredRoles from '../booking/modal/ingame/prefer/PreferredRoles'
import {
  ErrorResponse,
  readableError,
} from '../../types/errors/responses/error-response'
import { fetchFingerprint } from '../../lib/fingerprint'

type Props = {
  profiles?: CategoryProfileResponse[]
  game: CategoryResponse
  getProfiles: any
  mode?: 'home' | 'modal'
  type?: 'adding' | 'editing' | 'guest'
  title?: string
}

export interface TriggerSaveAccountFunction {
  triggerSubmit: () => void
}

const EditInGameAccountSection = forwardRef<TriggerSaveAccountFunction, Props>(
  ({ profiles, game, getProfiles, mode, type }: Props) => {
    const { t: common } = useTranslation('common')
    const { t: booking } = useTranslation('booking')
    const { t: inGameModal } = useTranslation('inGameModal')
    const [, setModalStatus] = useRecoilState(InGameAccountModalState)
    const [selectedId, setSelectedId] = useRecoilState(
      selectCategoryProfileState,
    )
    const [groupedTags] = useRecoilState(categoryGroupTagsState)
    const [isLoading, setIsLoading] = useState(false)
    const currentRolesState = useState([])
    const [currentRoles] = currentRolesState
    const [, setIsOpen] = useRecoilState(isOpenInGameAccountModalState)
    const [, setCurrentStep] = useRecoilState(homeStepsState)
    const [, setIsOpenSignUp] = useRecoilState(isOpenSignUpModalState)
    const { status } = useSession()
    const [selectedProduct] = useRecoilState(selectedProductState)
    const [, setIsOpenStripePayment] = useRecoilState(isOpenGuestPaymentState)
    const form = useForm()
    const {
      register,
      handleSubmit,
      getValues,
      setValue,
      trigger,
      reset,
      formState: { errors },
    } = form
    const [selectedAddons] = useRecoilState(selectedProductAddOnsState)

    const { mutate: deleteProfile, isSuccess: deleteSuccess } =
      useDeleteCategoryById(selectedId)
    const {
      mutate: createProfile,
      isSuccess: createProfileSuccess,
      error: createError,
    } = usePostCreateCategoryProfile(game.key)
    const {
      mutate: updateProfile,
      isSuccess: updateProfileSuccess,
      error: updateError,
    } = usePutUpdateCategoryProfile()

    useEffect(() => {
      if (updateError) {
        toast.error(readableError(updateError as ErrorResponse))
      }
    }, [updateError])

    useEffect(() => {
      if (createError) {
        toast.error(readableError(createError as ErrorResponse))
      }
    }, [createError])

    const createGuestAccount = async () => {
      const response = await guestSignIn()
      if (response?.error) {
        setIsOpen(false)
        setIsOpenSignUp(true)
        toast.error(
          'Apologies for the inconvenience. Please try signing up again to continue.',
        )
      }

      if (response?.ok) {
        setIsOpen(false)
        setIsOpenStripePayment(true)
      }
      setIsLoading(false)
    }

    useEffect(() => {
      if (createProfileSuccess) {
        getProfiles()
        toast.success('Saved!')
        setIsOpen(false)
      }

      if (deleteSuccess || createProfileSuccess || updateProfileSuccess) {
        getProfiles()
        setModalStatus(InGameAccountModalStatusEnum.ACCOUNTS)
        setSelectedId(-1)
        if (mode === 'home') {
          setCurrentStep(3)
          setModalStatus(InGameAccountModalStatusEnum.ACCOUNTS)
        }
      }
    }, [deleteSuccess, createProfileSuccess, updateProfileSuccess])

    useEffect(() => {
      let profile = profiles?.find((item) => {
        return item.id === selectedId
      })

      if (profile) {
        setValue('username', profile.username)
        setValue(
          'server',
          profile.categorySpecializedTags.find((item) => {
            return item.parent.name === 'Regions'
          })?.id,
        )
      } else {
        reset()
      }

      if (profile && profile.categorySpecializedTags) {
        let roles = groupBy(
          profile.categorySpecializedTags,
          (item) => item.parent.name,
        )

        const ranks = roles['Rank']
        if (ranks && ranks[0]) {
          setValue('rank', ranks[0].id)
        }
      }
    }, [profiles, selectedId])

    function getAllKeyByValue(object: any, value: any) {
      return Object.keys(object).filter((key) => object[key] === value)
    }

    async function guestSignIn() {
      const data = getValues()
      let userTestStatus: CategorySpecializedTag[] = []
      getAllKeyByValue(currentRoles, true).forEach(function (value) {
        userTestStatus = [
          ...userTestStatus,
          { id: +value, categoryKey: game.key } as CategorySpecializedTag,
        ]
      })

      if (data.server) {
        let serverTag = {
          id: +data.server,
          categoryKey: game.key,
        } as CategorySpecializedTag
        userTestStatus = [...userTestStatus, serverTag]
      }

      if (data.rank && data.rank !== -1 && data.rank !== '-1') {
        let rankTag = {
          id: +data.rank,
          categoryKey: game.key,
        } as CategorySpecializedTag
        userTestStatus = [...userTestStatus, rankTag]
      }

      let visitorId = null
      let fingerprintRequestId = null
      try {
        const fingerprintData = await fetchFingerprint()
        visitorId = fingerprintData?.visitorId
        fingerprintRequestId = fingerprintData?.requestId
      } catch (e) {}

      return await signIn('credentials', {
        redirect: false,
        body: JSON.stringify({
          username: data.username,
          categoryKey: game.key,
          categorySpecializedTags: userTestStatus,
          isSelected: true,
          isTeammate: false,
          addonIds: selectedAddons?.map((addon) => addon.id),
          visitorId: visitorId,
          fingerprintRequestId: fingerprintRequestId,
        }),
        grantType: 'guest',
      })
    }

    const onSubmit: SubmitHandler<any> = async (data) => {
      let userTestStatus: CategorySpecializedTag[] = []
      getAllKeyByValue(currentRoles, true).forEach(function (value) {
        userTestStatus = [
          ...userTestStatus,
          { id: +value, categoryKey: game.key } as CategorySpecializedTag,
        ]
      })

      if (data.server) {
        let serverTag = {
          id: +data.server,
          categoryKey: game.key,
        } as CategorySpecializedTag
        userTestStatus = [...userTestStatus, serverTag]
      }

      if (data.rank && data.rank !== -1 && data.rank !== '-1') {
        let rankTag = {
          id: +data.rank,
          categoryKey: game.key,
        } as CategorySpecializedTag
        userTestStatus = [...userTestStatus, rankTag]
      }

      if (selectedId > 0) {
        updateProfile({
          id: selectedId,
          username: data.username,
          categorySpecializedTags: userTestStatus,
        })
      } else {
        createProfile({
          username: data.username,
          categorySpecializedTags: userTestStatus,
          isSelected: true,
          isTeammate: false,
        })
      }

      updateStoredControl({
        selectedGame: game,
        selectedProduct: selectedProduct,
      } as UserLocalstorage)

      return
    }

    return (
      <div className={'mt-5 px-4 md:px-8'}>
        <form className={'mt-5'} id='account' onSubmit={handleSubmit(onSubmit)}>
          <div>
            <div className={'flex flex-row items-center gap-2.5'}>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='20'
                height='20'
                viewBox='0 0 20 20'
                fill='none'
              >
                <path
                  d='M15.1166 18.0167C14.3833 18.2333 13.5166 18.3334 12.5 18.3334H7.49997C6.4833 18.3334 5.61663 18.2333 4.8833 18.0167C5.06663 15.85 7.29163 14.1417 9.99997 14.1417C12.7083 14.1417 14.9333 15.85 15.1166 18.0167Z'
                  stroke='#050608'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M12.5001 1.66669H7.50008C3.33341 1.66669 1.66675 3.33335 1.66675 7.50002V12.5C1.66675 15.65 2.61675 17.375 4.88342 18.0167C5.06675 15.85 7.29175 14.1417 10.0001 14.1417C12.7084 14.1417 14.9334 15.85 15.1167 18.0167C17.3834 17.375 18.3334 15.65 18.3334 12.5V7.50002C18.3334 3.33335 16.6667 1.66669 12.5001 1.66669ZM10.0001 11.8083C8.35008 11.8083 7.01675 10.4667 7.01675 8.8167C7.01675 7.1667 8.35008 5.83335 10.0001 5.83335C11.6501 5.83335 12.9834 7.1667 12.9834 8.8167C12.9834 10.4667 11.6501 11.8083 10.0001 11.8083Z'
                  stroke='#050608'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M12.9833 8.81666C12.9833 10.4667 11.6499 11.8083 9.99994 11.8083C8.34994 11.8083 7.0166 10.4667 7.0166 8.81666C7.0166 7.16666 8.34994 5.83331 9.99994 5.83331C11.6499 5.83331 12.9833 7.16666 12.9833 8.81666Z'
                  stroke='#050608'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>

              <label
                htmlFor='username'
                className='text-body-medium text-fizz-1000'
              >
                {game.key === 'counter-strike-global-offensive'
                  ? 'Steam ID/Friend code'
                  : game.key === 'fifa'
                    ? 'EA ID/Platform ID'
                    : game.key === 'fortnite'
                      ? 'Epic display name'
                      : game.key === 'call-of-duty-warzone' ||
                          game.key === 'diablo-4'
                        ? booking('inGameBattleTagTitle')
                        : booking('inGameUserNameTitle')}
              </label>
            </div>

            {game.key === GameCategories.leagueOfLegends && (
              <p className={'mt-1 text-secondary-medium text-bult-500'}>
                Please use the right IGN so we can retrieve your rank profile
              </p>
            )}
          </div>

          <div className='relative mt-2 rounded-md shadow-sm'>
            <input
              type='text'
              id={'username'}
              {...register('username', { required: true })}
              className='input-in-game-name-v2 peer w-full'
              placeholder={'Enter username'}
            />
          </div>

          {errors.username && (
            <span className={'text-error'}>
              {common('inputFieldRequiredError')}
            </span>
          )}

          {groupedTags['Regions']?.length > 0 && (
            <div className={'mt-4'}>
              <div className={'flex flex-row items-center gap-2.5'}>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='20'
                  height='20'
                  viewBox='0 0 20 20'
                  fill='none'
                >
                  <path
                    d='M10.0001 18.3334C14.6025 18.3334 18.3334 14.6024 18.3334 10C18.3334 5.39765 14.6025 1.66669 10.0001 1.66669C5.39771 1.66669 1.66675 5.39765 1.66675 10C1.66675 14.6024 5.39771 18.3334 10.0001 18.3334Z'
                    stroke='#050608'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path
                    d='M6.66667 2.5H7.5C5.875 7.36667 5.875 12.6333 7.5 17.5H6.66667'
                    stroke='#050608'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path
                    d='M12.5 2.5C14.125 7.36667 14.125 12.6333 12.5 17.5'
                    stroke='#050608'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path
                    d='M2.5 13.3333V12.5C7.36667 14.125 12.6333 14.125 17.5 12.5V13.3333'
                    stroke='#050608'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path
                    d='M2.5 7.5C7.36667 5.875 12.6333 5.875 17.5 7.5'
                    stroke='#050608'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>

                <div className='text-body-medium text-fizz-1000'>
                  {booking('serverLabelTitle')}
                </div>
              </div>

              <div className='relative mt-2'>
                <select
                  id={'server'}
                  className='input-basic-select peer'
                  {...register('server', { required: true, min: 1 })}
                >
                  {groupedTags['Regions']?.map((item) => {
                    return (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    )
                  })}
                </select>
              </div>

              {errors.server && (
                <span className={'text-error'}>
                  {common('inputFieldRequiredError')}
                </span>
              )}
            </div>
          )}

          {groupedTags['Roles']?.length > 0 && (
            <div className={'mt-4'}>
              <PreferredRoles
                profile={profiles?.find((item) => {
                  return item.id === selectedId
                })}
                currentRolesState={currentRolesState}
              />
            </div>
          )}

          {selectedId > 0 && (
            <div
              className={
                'mt-5 flex w-fit cursor-pointer flex-row items-center gap-1'
              }
              onClick={(e) => {
                e.preventDefault()
                deleteProfile()
              }}
            >
              <div className={'relative h-6 w-6'}>
                <Image
                  src={TrashIcon}
                  fill={true}
                  className={'object-contain'}
                  alt={'Trash icon'}
                />
              </div>

              <p className={'text-body-normal text-color-gray3'}>
                {inGameModal('deleteAccountBtnTitle')}
              </p>
            </div>
          )}

          {type !== 'guest' ? (
            <button
              className={'btn-v2-primary no-animation mt-5 w-full'}
              type={'submit'}
              form='account'
            >
              {common('saveBtn')}
            </button>
          ) : (
            <button
              className={'btn-v2-primary no-animation mt-5 w-full'}
              onClick={(e) => {
                e.preventDefault()
                if (isLoading) return
                setIsLoading(true)
                trigger().then((res) => {
                  if (res) {
                    createGuestAccount()
                  } else {
                    setIsLoading(false)
                  }
                })
              }}
              form='account'
            >
              {isLoading && <span className='loading loading-spinner' />}
              {common('continueBtn')}
            </button>
          )}

          {type === 'guest' && (
            <div
              className={'text-secondary-normal mt-5 text-center text-black'}
            >
              If you would like to save your preferences for future sessions,{' '}
              <span
                className={'cursor-pointer font-bold underline'}
                onClick={(e) => {
                  e.preventDefault()
                  if (status === SessionStatusEnum.UNAUTHENTICATED) {
                    setIsOpenSignUp(true)
                    setIsOpen(false)
                    return
                  }
                }}
              >
                create an account.
              </span>
            </div>
          )}
        </form>
      </div>
    )
  },
)
export default EditInGameAccountSection
